import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Eye, X, UserCircle, Phone, Mail, Calendar, Shield, Hash, Search, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/DashLayout";
import { api } from "@/lib/api";
import Pagination from "@/components/Pagination";

// DetailRow Component for View Modal
const DetailRow = ({ icon: Icon, label, value, mono, small }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-white border border-slate-100">
    <div className="p-2 bg-slate-50 text-brand-primary rounded-md shrink-0"><Icon className="w-4 h-4" /></div>
    <div className="min-w-0 flex-1">
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{label}</div>
      <div className={`font-medium text-slate-700 truncate ${mono ? 'font-mono' : ''} ${small ? 'text-xs' : 'text-sm'}`}>{value}</div>
    </div>
  </div>
);

export default function Agents() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "" });
  const [show, setShow] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", mobile: "", status: "active" });
  const [resetPwTarget, setResetPwTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', PAGE_SIZE);
    if (search) params.set('search', search);
    api.get(`/admin/agents?${params.toString()}`).then(r => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [page, search]);
  
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try { 
      await api.post("/admin/agents", form); 
      toast.success("Added"); 
      setShow(false); 
      load(); 
      setForm({ name: "", email: "", mobile: "", password: "" }); 
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => { 
    if (!window.confirm("Delete this agent?")) return; 
    await api.delete(`/admin/agents/${id}`); 
    toast.success("Deleted"); 
    load(); 
  };

  // ── Edit ──
  const openEdit = (d) => {
    setEditTarget(d);
    setEditForm({
      name: d.user?.name || "",
      email: d.user?.email || "",
      mobile: d.user?.mobile || "",
      status: d.status || "active",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/agents/${editTarget.id}`, editForm);
      toast.success("Updated successfully");
      setEditTarget(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to update");
    }
  };

  const openResetPw = (d) => {
    setResetPwTarget(d);
  };

  const saveResetPw = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/agents/${resetPwTarget.id}/reset-password`, {});
      toast.success("Password reset and sent via notification!");
      setResetPwTarget(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to reset password");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Agents</h3>
        <button onClick={() => setShow(!show)} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold px-4 py-2 rounded-md">
          <Plus className="w-4 h-4" /> Add Agent
        </button>
      </div>

      {/* Add Form */}
      {show && (
        <form onSubmit={create} className="bg-white border border-slate-200 rounded-xl p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Name</label><input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded-md px-3 py-2 border-slate-200" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Email</label><input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded-md px-3 py-2 border-slate-200" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Mobile</label><input required placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full border rounded-md px-3 py-2 border-slate-200" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Password</label><input required type="password" minLength={6} placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border rounded-md px-3 py-2 border-slate-200" /></div>
          <div className="md:col-span-2 flex justify-end">
            <button className="bg-brand-accent hover:bg-brand-accentHover text-white rounded-md px-6 py-2 font-semibold">Save Agent</button>
          </div>
        </form>
      )}

      {/* Search Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="Search agents by name, email, or mobile..."
            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
        </div>
        <span className="text-xs text-slate-400">{rows.length} of {total}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left px-6 py-3">Mobile</th>
              <th className="text-left px-6 py-3">Role</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-right px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-6 py-4">
                  <div className="font-semibold text-brand-nav">{d.user?.name || '—'}</div>
                  {d.user?.email && <div className="text-xs text-slate-400 mt-0.5">{d.user.email}</div>}
                </td>
                <td className="px-6 py-4 font-mono text-slate-600">{d.user?.mobile || '—'}</td>
                <td className="px-6 py-4"><Badge tone="neutral">Agent</Badge></td>
                <td className="px-6 py-4"><Badge tone={d.status === "active" ? "success" : "warn"}>{d.status}</Badge></td>
                <td className="px-6 py-4 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button onClick={() => openResetPw(d)} title="Reset Password" className="p-2 text-orange-500 hover:bg-orange-50 rounded-md transition-colors">
                      <Lock className="w-4 h-4" />
                    </button>
                    <button onClick={() => setViewTarget(d)} title="View Details" className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-md transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(d)} title="Edit" className="p-2 text-amber-600 hover:bg-amber-50 rounded-md transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(d.id)} title="Delete" className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">{search ? 'No results match your search.' : 'No agents yet'}</td></tr>}
          </tbody>
        </table>
        <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
      </div>

      {/* ── View Details Modal ── */}
      {viewTarget && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setViewTarget(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-br from-brand-primary to-brand-accent px-6 py-5 text-white relative">
              <button onClick={() => setViewTarget(null)} className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <UserCircle className="w-8 h-8" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-xl">{viewTarget.user?.name || '—'}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge tone="neutral">Agent</Badge>
                    <Badge tone={viewTarget.status === "active" ? "success" : "warn"}>{viewTarget.status}</Badge>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <DetailRow icon={Phone} label="Mobile" value={viewTarget.user?.mobile || '—'} mono />
              <DetailRow icon={Mail} label="Email" value={viewTarget.user?.email || 'Not provided'} />
              <DetailRow icon={Shield} label="Role" value={viewTarget.user?.role || "agent"} />
              <DetailRow icon={Hash} label="Agent ID" value={viewTarget.id} mono small />
              <DetailRow icon={Hash} label="User ID" value={viewTarget.user_id || viewTarget.user?.id || '—'} mono small />
              <DetailRow icon={Calendar} label="Created" value={viewTarget.created_at ? new Date(viewTarget.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} />
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex gap-3">
              <button onClick={() => { setViewTarget(null); openEdit(viewTarget); }} className="flex-1 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-lg px-4 py-2 font-semibold transition-colors inline-flex items-center justify-center gap-2">
                <Pencil className="w-4 h-4" /> Edit
              </button>
              <button onClick={() => setViewTarget(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-lg px-4 py-2 font-semibold hover:bg-white transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editTarget && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setEditTarget(null)}>
          <form onSubmit={saveEdit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <h4 className="font-heading font-bold text-xl text-brand-nav mb-1">Edit Agent</h4>
            <p className="text-sm text-slate-500 mb-6">Update details for <strong>{editTarget.user?.name}</strong></p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
                <input required value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile *</label>
                  <input required type="tel" value={editForm.mobile} onChange={e => setEditForm({ ...editForm, mobile: e.target.value })}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button type="button" onClick={() => setEditTarget(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 bg-brand-accent hover:bg-brand-accentHover text-white rounded-md px-4 py-2 font-semibold transition-colors">Save Changes</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      {resetPwTarget && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setResetPwTarget(null)}>
          <form onSubmit={saveResetPw} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-xl">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-orange-600" />
              </div>
              <h4 className="font-heading font-bold text-xl text-brand-nav">Reset Password</h4>
            </div>
            <p className="text-sm text-slate-500 mb-6 pl-13">Are you sure you want to reset the password for <strong>{resetPwTarget.user?.name}</strong>? A new password will be generated and sent via SMS/WhatsApp.</p>
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setResetPwTarget(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-md px-4 py-2 font-semibold transition-colors">Yes, Reset & Send</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
